<template>
  <div>
    <vue-dropzone
      id="dropzone"
      ref="dropzoneForm"
      :options="dropZoneOptions"
      :use-custom-slot="true"
      @vdropzone-sending="onSending"
      @vdropzone-complete="onComplete"
      @vdropzone-success="onSuccess"
      @vdropzone-removed-file="onRemoved"
      @vdropzone-error="onError"
    >
      <slot></slot>
    </vue-dropzone>
    <div class="text-danger" v-if="errorMsg">
      {{ errorMsg }}
    </div>
  </div>
</template>

<script>
import { ref } from "@vue/composition-api";
import store from "@/store";
import vue2Dropzone from "vue2-dropzone";

const defaultDropzonePreviewTemplate = `
<div class="my-2 mr-2 p-2 text-white dropzone-attachment-thumbnail">
  <span class="p-1" data-dz-name></span>
</div>
`;

export default {
  name: "Dropzone",
  components: {
    "vue-dropzone": vue2Dropzone,
  },
  props: {
    value: Array,
    previewContainerId: String,
    previewTemplateId: String,
  },
  setup(props, { emit, refs, root }) {
    // if you don't want to use the default preview template, define the identifier of which element to use here.
    const previewContainerId = props.previewContainerId || ".dropzone-previews";
    // if the id of a custom preview template is passed, find it in the dom
    const customTemplate = document.querySelector(`#${props.previewTemplateId}`);
    // if no custom template id was passed, use the default preview template
    const previewTemplate = customTemplate
      ? customTemplate.innerHTML
      : defaultDropzonePreviewTemplate;
    // config for dropzone library
    const dropZoneOptions = {
      url: `${process.env.VUE_APP_API_URL || ""}/attachment/new`, // nocworx endpoint we send attachments to
      clickable: "#dropzone-upload-file", // id of the button that triggers an browse/upload
      maxFilesize: 64,
      thumbnailWidth: 50,
      addRemoveLinks: true,
      dictCancelUpload: root.$t("global.cancel"), // what to show on the canel button that appears during upload
      dictRemoveFile: "&times;", // what character(s) to use for the remove button on the upload preview thumbnail
      previewsContainer: previewContainerId, // identifier for which element to show the upload previews in
      previewTemplate: previewTemplate,
      headers: { Authorization: `Bearer ${store.state.auth.token.fullString}` },
    };

    const files = ref([]);
    const errorMsg = ref(null);

    const onSending = (file) => {
      errorMsg.value = null;

      const existingFile = files.value.find((f) => f.name === file.name);

      // if file has bee selected already, or if the new attachment has a duplicate file name, don't allow upload
      if (existingFile) {
        // inform dropzone that the file should not be included
        refs.dropzoneForm.removeFile(file);
        // display error
        errorMsg.value = root.$t("global.could-not-upload-duplicate-file-name", {
          name: existingFile.name,
        });
        return;
      }

      emit("sending", true);
    };

    const onComplete = () => {
      emit("complete", false);
    };

    // should display a generic error if dropzone fails to upload the file
    const onError = (file) => {
      errorMsg.value = root.$t("global.could-not-upload-file", { name: file.name });
      // tell dropzone to remove the file from the list
      refs.dropzoneForm.removeFile(file);
    };

    const onSuccess = (file, attachment) => {
      // save the attachment in a list
      // attachment name is identical to file name - that's how we'll re-associate attachments to file
      files.value.push(attachment);
      const fileIds = files.value.map((f) => f.id);

      emit("input", fileIds);
    };

    const onRemoved = (file) => {
      // find file by its name
      const removedIndex = files.value.findIndex((f) => f.name === file.name);
      // remove from list
      files.value.splice(removedIndex, 1);

      const fileIds = files.value.map((f) => f.id);
      emit("input", fileIds);
    };

    const removeAllFiles = (cancel) => {
      refs.dropzoneForm.removeAllFiles(cancel);
    };

    return {
      dropZoneOptions,
      onSending,
      onComplete,
      onSuccess,
      onRemoved,
      onError,
      errorMsg,
      removeAllFiles,
    };
  },
};
</script>
<style lang="scss">
.dropzone-attachment-thumbnail {
  display: inline-block;
}

.dropzone-previews {
  margin-top: 2px;
  display: inline-block;
}
</style>
